import { useState, useEffect, useCallback } from 'react';
import type { ProviderType } from '@/ai/providers';
import {
  getDefaultProvider,
  setDefaultProvider,
  hasProviderApiKey,
  getAvailableProviders,
} from '@/ai/providers';

/**
 * Hook for managing AI provider preferences
 * 
 * Provides:
 * - Current provider preference
 * - Available providers (those with API keys)
 * - Functions to change provider
 * - Provider availability status
 */
export function useAIProvider() {
  const [currentProvider, setCurrentProvider] = useState<ProviderType>(() => getDefaultProvider());
  const [availableProviders, setAvailableProviders] = useState<ProviderType[]>(() => getAvailableProviders());

  // Update available providers when API keys change
  useEffect(() => {
    const checkProviders = () => {
      const available = getAvailableProviders();
      setAvailableProviders(available);
      
      // If current provider is no longer available, switch to default
      if (!available.includes(currentProvider)) {
        const defaultProvider = getDefaultProvider();
        setCurrentProvider(defaultProvider);
      }
    };

    // Check on mount
    checkProviders();

    // Listen for storage changes (when API keys are updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'openai_api_key' || e.key === 'gemini_api_key') {
        checkProviders();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically (for same-tab updates)
    const interval = setInterval(checkProviders, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentProvider]);

  const changeProvider = useCallback((provider: ProviderType) => {
    if (!hasProviderApiKey(provider)) {
      throw new Error(`API key for ${provider} is not configured`);
    }
    
    setDefaultProvider(provider);
    setCurrentProvider(provider);
  }, []);

  const isProviderAvailable = useCallback((provider: ProviderType) => {
    return hasProviderApiKey(provider);
  }, []);

  return {
    currentProvider,
    availableProviders,
    changeProvider,
    isProviderAvailable,
    hasOpenAI: hasProviderApiKey('openai'),
    hasGemini: hasProviderApiKey('gemini'),
  };
}












